import { useEffect, useRef, useState } from "react";
import { BsFillPlayFill } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import styled from "styled-components";
import { twMerge } from "tailwind-merge";
import { useHandleClickOutside } from "./hook/useHandleClickOutside";
import useProductStore from "./zustand/useProductStore";
import { toast } from "react-toastify";
import { MdFullscreen } from "react-icons/md";
import endpoint from "../../utility/endpoints";
import { useSas } from "../../lib/store/useSasStore";

const Wrapper = styled.div``;

const PlayIcon = ({
  bg = "bg-white",
  fill = "black",
  border = "!border-2 !border-black",
  iconSize = 50,
  className,
}) => {
  const {
    ref: modalRef,
    isOpen: showVideoModal,
    setIsOpen: setShowVideoModal,
  } = useHandleClickOutside();
  const { productDetails } = useProductStore();
  const videoRef = useRef();
   const {sasToken}=useSas()

  const captionsUrl = `${productDetails?.caption?.split("?")?.[0]}?${sasToken}`;

  const [showButton, setShowButton] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [currentCaption, setCurrentCaption] = useState("");
  const [captionsEnabled, setCaptionsEnabled] = useState(true);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLabel, setShowLabel] = useState(false);

  const handleCloseVideoModal = (e) => {
    e.stopPropagation();
    setShowVideoModal(false);
  };

  useEffect(() => {
    if (showVideoModal) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "initial";
    }

    if (videoRef.current) {
      if (showVideoModal) {
        videoRef.current?.play();
      } else {
        videoRef.current?.pause();
      }
    }
  }, [showVideoModal]);

  useEffect(() => {
    if (Object.keys(productDetails).length !== 0 && !captionsUrl) {
      toast.error("Captions not available for this video");
      return;
    }
    const fetchCaptions = async () => {
      try {
        const response = await fetch(captionsUrl);
        if (!response.ok) throw new Error("Failed to fetch captions");
        const text = await response.text();
        const parsedCaptions = parseVtt(text);

        console.log("parsedCaptions", parsedCaptions);

        setCaptions(parsedCaptions);
      } catch (error) {
        console.error("Error fetching captions:", error);
      }
    };

    fetchCaptions();
  }, [captionsUrl]);

  const parseVtt = (vttText) => {
    const lines = vttText.split(/\r?\n/);
    const captions = [];
    let caption = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!line) continue;

      const timeMatch = line.match(
        /^(\d{2}:\d{2}:\d{2}[\.,]\d{3}) --> (\d{2}:\d{2}:\d{2}[\.,]\d{3})/
      );

      if (timeMatch) {
        if (caption) captions.push(caption);

        caption = {
          start: parseTime(timeMatch[1]),
          end: parseTime(timeMatch[2]),
          text: "",
        };
      } else if (caption) {
        let cleanedText = line.replace(/<[^>]+>/g, "").trim(); // Remove HTML tags
        cleanedText = cleanedText.replace(
          /\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-\d+\b/g,
          ""
        ); // Remove UUID-like strings

        caption.text += (caption.text ? " " : "") + cleanedText.trim();
      }
    }

    // Push the last caption if valid
    if (caption) captions.push(caption);

    return captions;
  };

  // Convert VTT time format (hh:mm:ss,ms) to seconds
  const parseTime = (timeString) => {
    return timeString
      .replace(",", ".") // Ensure we handle both `,` and `.`
      .split(":")
      .reduce((acc, time) => 60 * acc + parseFloat(time), 0);
  };

  const updateCaptions = () => {
    if (videoRef.current && captions.length > 0) {
      const currentTime = videoRef.current.currentTime;
      const activeCaption = captions.find(
        (caption) => currentTime >= caption.start && currentTime <= caption.end
      );
      setCurrentCaption(activeCaption ? activeCaption.text : "");
    }
  };

  const handleFullScreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      <Wrapper
        className={`${
          showVideoModal ? "visible opacity-100 " : "invisible opacity-0"
        } transition-all duration-500 !fixed !z-[999999999999999] left-0 top-0 !w-screen !h-screen !rounded-none bg-black bg-opacity-70 `}
      >
        <div
          ref={modalRef}
          className={`bg-white ${
            isFullscreen ? "w-screen h-screen" : "w-[80vw] h-[86vh]"
          } flex justify-center items-center mx-auto !rounded-md shadow-lg absolute !z-[999999999999999] top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 transition-all`}
          onMouseEnter={() => setShowButton(true)}
          onMouseLeave={() => setShowButton(false)}
        >
          <div className="bg-red-500 absolute !right-0 !top-0 p-1 z-10 cursor-pointer">
            <RxCross2 onClick={handleCloseVideoModal} size={22} color="#fff" />
          </div>

          <div
            onClick={handleFullScreen}
            onMouseOver={() => setShowLabel(true)}
            onMouseLeave={() => setShowLabel(false)}
            className={`${
              showButton ? "visible opacity-100" : "invisible opacity-0"
            } bg-[rgba(0,0,0,0.4)] absolute !left-0 !top-0 p-1 z-10 cursor-pointer`}
          >
            <MdFullscreen size={22} color="#fff" />
            <p
              className={`absolute left-[35px] top-1 whitespace-nowrap bg-[rgba(0,0,0,0.4)] text-white px-2 ${
                showLabel ? "visible" : "invisible"
              } transition-all`}
            >
              {isFullscreen ? "Normal Screen View" : "Full Screen View"}
            </p>
          </div>

          {productDetails?.video && productDetails?.video !== "null" ? (
            <>
              <CustomVideo
                controlsList="nodownload"
                ref={videoRef}
                src={productDetails.video}
                controls
                className="!h-full !w-full object-fill"
                onTimeUpdate={updateCaptions}
              />
              {captionsUrl && (
                <>
                  {captionsEnabled && currentCaption && (
                    <Captions>{currentCaption}</Captions>
                  )}
                  <ToggleButton
                    className={`${
                      showButton ? "visible opacity-100" : "invisible opacity-0"
                    } transition-all`}
                    onClick={() => setCaptionsEnabled(!captionsEnabled)}
                  >
                    {captionsEnabled
                      ? "Disable Captions [cc]"
                      : "Enable Captions [cc]"}
                  </ToggleButton>
                </>
              )}
            </>
          ) : (
            <h1 className="font-unilever-shilling-medium">No Video</h1>
          )}
        </div>
      </Wrapper>

      {productDetails?.video && productDetails?.video !== null && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowVideoModal(true);
          }}
          style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
          className={twMerge(
            `p-2 flex justify-center items-center !rounded-full ${bg} ${border} absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4 ${className}`
          )}
        >
          <BsFillPlayFill fill={fill} className="h-full w-full" />
        </button>
      )}
    </>
  );
};

export const CustomVideo = styled.video`
  &::-webkit-media-controls-download-button {
    display: none;
  }
  &::-webkit-media-controls-fullscreen-button {
    display: none; /* Hide default fullscreen button */
  }
`;

const Captions = styled.div`
  position: absolute;
  bottom: 50px;
  width: 100%;
  text-align: center;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  padding: 5px;
  font-size: 18px;
`;

const ToggleButton = styled.button`
  font-family: "Neue Machina Regular";
  font-size: 13px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 5px;
  padding: 6px 10px;
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
`;

export default PlayIcon;

